import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Devices.css";

const TYPE_OPTIONS = ["bulb", "plug", "strip", "other"];

function Devices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRoom, setFilterRoom] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/devices");
      setDevices(data.items || []);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleDelete = async (id) => {
    if (!(await confirm("Remove this device from registry?"))) return;
    try {
      await apiClient.delete(`/devices/${id}`);
      fetchDevices();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const rooms = [...new Set(devices.map((d) => d.room).filter(Boolean))];

  const filtered = devices.filter((d) => {
    if (filterRoom !== "all" && d.room !== filterRoom) return false;
    return true;
  });

  const typeIcon = (type) => {
    switch (type) {
      case "bulb": return "💡";
      case "plug": return "🔌";
      case "strip": return "🌈";
      default: return "📡";
    }
  };

  return (
    <div className="devices-page">
      {confirmDialog}
      <div className="devices-header">
        <h2>Devices</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <RefreshButton onClick={fetchDevices} title="Refresh devices" />
          <button className="btn-primary" onClick={handleAdd}>
            + Add Device
          </button>
        </div>
      </div>

      <div className="devices-filters">
        {rooms.length > 0 && (
          <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)}>
            <option value="all">All Rooms</option>
            {rooms.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        )}
        <span className="devices-count">
          {filtered.length} device{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {showForm && (
        <DeviceForm
          item={editItem}
          rooms={rooms}
          onSave={() => { setShowForm(false); fetchDevices(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="placeholder">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="placeholder">
          No devices registered yet. Add your WiZ devices to get started!
        </div>
      ) : (
        <div className="devices-list">
          {filtered.map((device) => (
            <div key={device.id} className="device-card">
              <div className="device-top">
                <span className="device-icon">{typeIcon(device.type)}</span>
                <span className="device-name">{device.name}</span>
                <div className="device-badges">
                  <span className="device-badge type-badge">{device.type}</span>
                  {device.online !== false && (
                    <span className="device-badge online-badge">online</span>
                  )}
                  {device.online === false && (
                    <span className="device-badge offline-badge">offline</span>
                  )}
                </div>
              </div>
              <div className="device-meta">
                {device.room && <span className="device-room">🏠 {device.room}</span>}
                <span className="device-mac">{device.mac}</span>
                {device.model && <span className="device-model">{device.model}</span>}
                <div className="device-actions">
                  <button className="btn-secondary btn-xs" onClick={() => handleEdit(device)}>Edit</button>
                  <button className="btn-danger btn-xs" onClick={() => handleDelete(device.id)}>Remove</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceForm({ item, rooms, onSave, onCancel }) {
  const isEditing = Boolean(item);
  const [form, setForm] = useState({
    name: item?.name || "",
    mac: item?.mac || "",
    ip: item?.ip || "",
    room: item?.room || "",
    type: item?.type || "bulb",
    model: item?.model || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await apiClient.put(`/devices/${item.id}`, form);
      } else {
        await apiClient.post("/devices", form);
      }
      onSave();
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="device-form">
      <h3>{isEditing ? "Edit" : "Add"} Device</h3>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Name *</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Bedroom Light"
            required
          />
        </div>

        <div className="form-group">
          <label>MAC Address *</label>
          <input
            value={form.mac}
            onChange={(e) => setForm({ ...form, mac: e.target.value })}
            placeholder="a8:bb:50:xx:xx:xx"
            required
            disabled={isEditing}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Room</label>
            <input
              value={form.room}
              onChange={(e) => setForm({ ...form, room: e.target.value })}
              placeholder="bedroom"
              list="room-suggestions"
            />
            <datalist id="room-suggestions">
              {rooms.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </div>

          <div className="form-group">
            <label>Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>IP Address</label>
            <input
              value={form.ip}
              onChange={(e) => setForm({ ...form, ip: e.target.value })}
              placeholder="192.168.1.45"
            />
          </div>

          <div className="form-group">
            <label>Model</label>
            <input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder="ESP15_SHTW9_01"
            />
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update" : "Add Device"}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default Devices;
